import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';

const MandatoryBadge = ({ isMandatory, size = 'md' }) => {
  if (!isMandatory) return null;
  const sizeClasses = { sm: 'px-2 py-0.5 text-xs', md: 'px-2.5 py-1 text-sm', lg: 'px-3 py-1.5 text-base' };
  return (
    <span className={`inline-flex items-center gap-1 ${sizeClasses[size]} rounded-md bg-amber-100 text-amber-800 font-semibold border border-amber-300`}>
      <span>⚠️</span>
      <span>Mandatory</span>
    </span>
  );
};

const getCurrencySymbol = (currency) => {
  switch (currency?.toUpperCase()) {
    case 'INR': return '₹';
    case 'USD': return '$';
    case 'SGD': return 'S$';
    case 'EUR': return '€';
    case 'GBP': return '£';
    default: return '$';
  }
};

function CourseCard({
  course,
  mode = 'browse',
  actionLabel,
  onAction,
  isActionLoading = false,
  lastRef = null,
  viewMode = 'grid',
}) {
  const [imgError, setImgError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const price = course.price === 'Free' ? 'Free' : (course.price ?? '0.00');
  const isFree = price === 'Free' || course.course_type?.toLowerCase() === 'free';

  const handleCopyUrl = async (e) => {
    e.stopPropagation();
    if (!course.course_url) {
      toast.error('Course URL not available', { position: 'top-center', autoClose: 2000 });
      return;
    }
    try {
      await navigator.clipboard.writeText(course.course_url);
      toast.success('URL copied to clipboard!', { position: 'top-center', autoClose: 2000 });
    } catch {
      toast.error('Failed to copy', { position: 'top-center', autoClose: 2000 });
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    if (!course.course_url) {
      toast.error('Course URL not available', { position: 'top-center', autoClose: 2000 });
      return;
    }
    try {
      if (navigator.share) {
        await navigator.share({
          title: course.title,
          text: course.description,
          url: course.course_url,
        });
      } else {
        await navigator.clipboard.writeText(course.course_url);
        toast.success('URL copied to clipboard!', { position: 'top-center', autoClose: 2000 });
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        await handleCopyUrl(e);
      }
    }
  };

  const handleCardClick = (e) => {
    if (e.target.closest('button')) return;
    onAction?.(course);
  };

  const displayActionLabel = actionLabel ?? (
    mode === 'my-course'
      ? (course.isExpired ? 'Renew' : (course.is_completed ? 'Completed' : 'Continue'))
      : 'View Course'
  );

  // List view layout
  if (viewMode === 'list') {
    return (
      <article
        ref={lastRef}
        onClick={handleCardClick}
        className="courses-page-card group relative bg-[var(--bg-primary)] rounded-xl overflow-hidden flex flex-row border border-[var(--border-color)] shadow-sm hover:shadow-lg hover:border-[var(--primary-lighter)] transition-all duration-300 cursor-pointer"
      >
        {/* Image */}
        <div className="relative w-48 h-32 flex-shrink-0 bg-[var(--bg-tertiary)] overflow-hidden">
          {!imageLoaded && <div className="absolute inset-0 bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200 animate-pulse" />}
          <img
            src={imgError ? 'https://placehold.co/400x250/e2e8f0/64748b?text=Course' : (course.image || 'https://placehold.co/400x250/e2e8f0/64748b?text=Course')}
            alt={course.title}
            className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImgError(true)}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20" />
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 p-4">
          <div className="flex items-start justify-between gap-4 mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-[var(--text-primary)] text-base leading-snug line-clamp-1 mb-1">
                {course.title || 'Untitled Course'}
              </h3>
              <p className="text-sm text-[var(--text-secondary)] line-clamp-2 leading-relaxed">
                {course.description || 'No description.'}
              </p>
              {course.instructor_name && (
                <p className="text-xs text-[var(--text-secondary)] mt-2 flex items-center gap-1.5">
                  <Icon icon="mdi:account-outline" width={14} height={14} className="text-[var(--primary-color)]" />
                  {course.instructor_name}
                </p>
              )}
            </div>
          </div>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-[var(--text-secondary)] mt-3">
            {course.is_mandatory === 1 && (
              <MandatoryBadge isMandatory={true} size="sm" />
            )}
            <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-[var(--bg-secondary)] text-[var(--text-primary)]">
              {course.level || 'All levels'}
            </span>
            <span className="flex items-center gap-1.5">
              <Icon icon="mdi:book-open-outline" width={16} height={16} className="text-[var(--primary-color)]" />
              {course.modules ?? 0} modules
            </span>
            <span className="flex items-center gap-1.5">
              <Icon icon="mdi:account-group-outline" width={16} height={16} className="text-[var(--primary-color)]" />
              {course.enrolled ?? 0} enrolled
            </span>
            <span className="flex items-center gap-1.5">
              <Icon icon="mdi:star" className="text-amber-500" width={16} height={16} />
              {Number(course.rating)?.toFixed(1) || '0.0'}
            </span>
          </div>

          {/* Footer */}
          <div className="mt-auto flex items-center justify-between gap-4 pt-3">
            {/* Price display hidden */}
            {/* {mode === 'browse' && (
              <div className={`text-base font-bold ${isFree ? 'text-emerald-600' : 'text-[var(--primary-color)]'}`}>
                {isFree ? 'Free' : (
                  <>
                    <span className="text-[var(--text-secondary)] font-normal">{getCurrencySymbol(course.currency)}</span>
                    {course.price}
                  </>
                )}
              </div>
            )} */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onAction?.(course); }}
              disabled={isActionLoading}
              className="ml-auto flex items-center justify-center gap-2 min-w-[130px] px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-[var(--primary-color)] hover:bg-[var(--primary-hover)] disabled:opacity-70 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
            >
              {isActionLoading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <Icon icon={course.isExpired ? 'mdi:refresh' : (mode === 'my-course' ? (course.is_completed ? 'mdi:check-circle' : 'mdi:play-circle') : (isFree ? 'mdi:play-circle' : 'mdi:lock-open'))} width={20} height={20} />
                  {displayActionLabel}
                </>
              )}
            </button>
          </div>
        </div>
      </article>
    );
  }

  // Grid view layout (default)
  return (
    <article
      ref={lastRef}
      onClick={handleCardClick}
      className="courses-page-card group relative bg-[var(--bg-primary)] rounded-2xl overflow-hidden flex flex-col h-full border border-[var(--border-color)] shadow-sm hover:shadow-xl hover:border-[var(--primary-lighter)] hover:-translate-y-1 transition-all duration-300 cursor-pointer"
    >
      {/* Image */}
      <div className="relative aspect-[16/10] bg-[var(--bg-tertiary)] overflow-hidden flex-shrink-0">
        {!imageLoaded && <div className="absolute inset-0 bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200 animate-pulse" />}
        <img
          src={imgError ? 'https://placehold.co/400x250/e2e8f0/64748b?text=Course' : (course.image || 'https://placehold.co/400x250/e2e8f0/64748b?text=Course')}
          alt={course.title}
          className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImgError(true)}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          {course.is_mandatory === 1 && (
            <MandatoryBadge isMandatory={true} size="sm" />
          )}
          <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-white/95 text-[var(--text-primary)] shadow-md backdrop-blur-sm">
            {course.level || 'All levels'}
          </span>
          {course.isExpired && (
            <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-red-500 text-white shadow-md">
              Expired
            </span>
          )}
          {mode === 'browse' && (
            <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold shadow-md bg-emerald-500 text-white`}>
              Free
            </span>
          )}
        </div>

      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-5">
        <h3 className="font-bold text-[var(--text-primary)] text-lg leading-snug line-clamp-2 mb-2 min-h-[3rem]">
          {course.title || 'Untitled Course'}
        </h3>
        <p className="text-sm text-[var(--text-secondary)] line-clamp-2 mb-4 flex-1 leading-relaxed">
          {course.description || 'No description.'}
        </p>

        {course.instructor_name && (
          <div className="flex items-center gap-2 mb-4 text-sm text-[var(--text-secondary)]">
            <Icon icon="mdi:account-outline" width={16} height={16} className="text-[var(--primary-color)]" />
            <span>{course.instructor_name}</span>
          </div>
        )}

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-[var(--text-secondary)] mb-5">
          <span className="flex items-center gap-1.5">
            <Icon icon="mdi:book-open-outline" width={16} height={16} className="text-[var(--primary-color)]" />
            {course.modules ?? 0} modules
          </span>
          <span className="flex items-center gap-1.5">
            <Icon icon="mdi:account-group-outline" width={16} height={16} className="text-[var(--primary-color)]" />
            {course.enrolled ?? 0} enrolled
          </span>
          <span className="flex items-center gap-1.5">
            <Icon icon="mdi:star" className="text-amber-500" width={16} height={16} />
            {Number(course.rating)?.toFixed(1) || '0.0'}
          </span>
        </div>

        {/* Footer */}
        <div className="mt-auto flex items-center justify-between gap-4 pt-4 border-t border-[var(--border-color)]">
          {/* Price display hidden */}
          {/* {mode === 'browse' ? (
            <div className={`text-base font-bold ${isFree ? 'text-emerald-600' : 'text-[var(--primary-color)]'}`}>
              {isFree ? 'Free' : (
                <>
                  <span className="text-[var(--text-secondary)] font-normal">{getCurrencySymbol(course.currency)}</span>
                  {course.price}
                </>
              )}
            </div>
          ) : course.progress !== undefined ? ( */}
          {course.progress !== undefined ? (
            <div className="flex items-center gap-2.5">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 shadow-sm"
                style={{ background: `conic-gradient(var(--primary-color) ${course.progress || 0}%, var(--bg-tertiary) 0)` }}
              >
                <div className="w-[26px] h-[26px] bg-[var(--bg-primary)] rounded-full flex items-center justify-center text-[9px] font-bold text-[var(--primary-color)]">
                  {course.progress || 0}%
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-[11px] text-[var(--text-secondary)] uppercase tracking-wider font-medium">Progress</span>
              </div>
            </div>
          ) : (
            <div />
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onAction?.(course); }}
            disabled={isActionLoading}
            className="ml-auto flex items-center justify-center gap-2 min-w-[130px] px-5 py-3 rounded-xl text-sm font-semibold text-white bg-[var(--primary-color)] hover:bg-[var(--primary-hover)] disabled:opacity-70 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
          >
            {isActionLoading ? (
              <>
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Loading...</span>
              </>
            ) : (
              <>
                <Icon icon={course.isExpired ? 'mdi:refresh' : (mode === 'my-course' ? (course.is_completed ? 'mdi:check-circle' : 'mdi:play-circle') : (isFree ? 'mdi:play-circle' : 'mdi:lock-open'))} width={22} height={22} />
                {displayActionLabel}
              </>
            )}
          </button>
        </div>
      </div>
    </article>
  );
}

export default CourseCard;
export { getCurrencySymbol };
